import { AsaasError } from "./exceptions"
import { createRequest } from "./requests"
import type {
  CreateCustomerData,
  CreateCustomerResponse,
  CreatePaymentData,
  CreatePaymentResponse,
  CreatePaymentWithCreditCardData,
  CreatePaymentWithCreditCardResponse,
  ListCustomersData,
  ListCustomersResponse,
  ShowIdentificationFieldResponse,
  ShowPaymentResponse,
  ShowPixQrCodeResponse,
} from "./types"
import { objectToQueryString } from "./utils"

function asaasjs({
  apiKey,
  sandbox = false,
}: {
  sandbox?: boolean
  apiKey: string
}) {
  if (!apiKey) throw new AsaasError("API key is required!")
  const request = createRequest(apiKey, sandbox)

  return {
    /**
     * Gerencie seus clientes.
     */
    customers: {
      /**
       * Permite que você recupere uma lista dos clientes criados.
       *
       * @returns Lista de clientes criados ou erro
       * @example
       * ```ts
       * const asaasjs = asaasjs('apiKey');
       *
       * const response = await asaasjs.customers.list();
       * /* ... * /
       */
      list(data: ListCustomersData = {}): Promise<ListCustomersResponse> {
        return request(`/customers?${objectToQueryString(data)}`, {
          method: "GET",
        })
      },

      /**
       * Permite que você cadastre um novo cliente
       *
       * @returns Cliente criado ou erro
       * @example
       * ```ts
       * const asaasjs = asaasjs('apiKey');
       *
       * const response = await asaasjs.customers.create(data);
       * /* ... * /
       */
      create(data: CreateCustomerData): Promise<CreateCustomerResponse> {
        return request(`/customers`, {
          method: "POST",
          body: JSON.stringify(data),
        })
      },
    },

    /**
     * Gerencie suas cobranças.
     */
    payments: {
      /**
       * Permite que você crie uma nova cobrança
       *
       * @returns Cobrança criada ou erro
       * @example
       * ```ts
       * const asaasjs = asaasjs('apiKey');
       *
       * const response = await asaasjs.payments.create(data);
       * /* ... * /
       */
      create(data: CreatePaymentData): Promise<CreatePaymentResponse> {
        return request(`/payments`, {
          method: "POST",
          body: JSON.stringify(data),
        })
      },

      /**
       * Permite que você crie uma nova cobrança pagando com cartão de crédito
       *
       * @returns Cobrança criada ou erro
       * @example
       * ```ts
       * const asaasjs = asaasjs('apiKey');
       *
       * const response = await asaasjs.payments.create(data);
       * /* ... * /
       */
      createWithCreditCard(
        data: CreatePaymentWithCreditCardData
      ): Promise<CreatePaymentWithCreditCardResponse> {
        return request(`/payments`, {
          method: "POST",
          body: JSON.stringify(data),
        })
      },

      /**
       * Permite que você recupere uma única cobrança
       *
       * @returns Cobrança ou erro
       * @example
       * ```ts
       * const asaasjs = asaasjs('apiKey');
       *
       * const response = await asaasjs.payments.show(cobrancaId);
       * /* ... * /
       */
      show(id: string): Promise<ShowPaymentResponse> {
        return request(`/payments/${id}`, {
          method: "GET",
        })
      },

      /**
       * Permite que você recupere a linha digitável de uma cobrança única
       *
       * @returns Linha digitável ou erro
       * @example
       * ```ts
       * const asaasjs = asaasjs('apiKey');
       *
       * const response = await asaasjs.payments.showIdentificationField(cobrancaId);
       * /* ... * /
       */
      showIdentificationField(
        id: string
      ): Promise<ShowIdentificationFieldResponse> {
        return request(`/payments/${id}/identificationField`, {
          method: "GET",
        })
      },

      /**
       * Permite que você recupere o QR Code Pix de uma cobrança única
       *
       * @returns QRCode ou erro
       * @example
       * ```ts
       * const asaasjs = asaasjs('apiKey');
       *
       * const response = await asaasjs.payments.showPixQrCode(cobrancaId);
       * /* ... * /
       */
      showPixQrCode(id: string): Promise<ShowPixQrCodeResponse> {
        return request(`/payments/${id}/pixQrCode`, {
          method: "GET",
        })
      },
    },
  }
}

export { AsaasError, asaasjs }
